import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar,
    CircularProgress,
    InputAdornment,
    Typography,
    Divider
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import userService from '../../../services/userServices';
import roleAdminService from '../../../services/roleAdminService';

const UserRoleAssignDialog = ({ open, onClose, onAssign, roleId, roleName }) => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open) {
            fetchUsers();
        }
    }, [open]);

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredUsers(users);
        } else {
            const filtered = users.filter(user =>
                user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredUsers(filtered);
        }
    }, [searchTerm, users]);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            // Récupérer tous les utilisateurs
            const allUsers = await userService.getAllUsers();

            // Récupérer les utilisateurs qui ont déjà ce rôle
            const roleUsers = await roleAdminService.getRoleUsers(roleId);
            const roleUserIds = roleUsers.map(user => user.id);

            // Filtrer pour ne garder que les utilisateurs qui n'ont pas encore ce rôle
            const eligibleUsers = allUsers.filter(user => !roleUserIds.includes(user.id));

            setUsers(eligibleUsers);
            setFilteredUsers(eligibleUsers);
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleSelectUser = (userId) => {
        onAssign(userId);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Attribuer le rôle {roleName}
            </DialogTitle>
            <DialogContent dividers>
                <TextField
                    autoFocus
                    margin="dense"
                    placeholder="Rechercher un utilisateur..."
                    fullWidth
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                        <CircularProgress />
                    </div>
                ) : filteredUsers.length === 0 ? (
                    <Typography variant="body1" align="center" sx={{ py: 3 }}>
                        {searchTerm.trim() !== ''
                            ? 'Aucun utilisateur trouvé pour cette recherche'
                            : 'Tous les utilisateurs ont déjà ce rôle'}
                    </Typography>
                ) : (
                    <List sx={{ pt: 1 }}>
                        {filteredUsers.map((user) => (
                            <React.Fragment key={user.id}>
                                <ListItem button onClick={() => handleSelectUser(user.id)}>
                                    <ListItemAvatar>
                                        <Avatar src={user.profilePicture} alt={user.name}>
                                            {user.name.charAt(0)}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={user.name}
                                        secondary={user.email}
                                    />
                                </ListItem>
                                <Divider />
                            </React.Fragment>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    Fermer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserRoleAssignDialog;